import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  createMuiTheme,
  Divider,
  Snackbar,
  TextField,
  ThemeProvider,
  Typography,
} from "@material-ui/core";
import { blueGrey, red } from "@material-ui/core/colors";
import { CheckBox } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";
import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAppState } from "../../components/AppProvider/AppProvider";
import { usePermission } from "../../components/Permission/Permission";
import { PERMISSIONS } from "../../helpers/permissions";
import {
  ExtTalentaUser,
  intlFormat,
  makeDateStringUTC,
} from "../../helpers/helpers";
import {
  fetchApps,
  fetchBanks,
  fetchCompanies,
  fetchDepartments,
  fetchExtTalentaUsers,
  fetchHrEmployees,
  fetchHrEmployeesDetail,
  fetchPph21MarriageCategories,
  fetchRoles,
  fetchUser,
  fetchUserApps,
} from "../../helpers/fetchers";
import filterNotHiddenAndSort from "../../helpers/filterNotHiddenAndSort";
import {
  initialApp,
  initialDepartment,
  initialPph21,
  initialUser,
  initialUserApp,
  initialUserDepartment,
} from "../../helpers/modelinitials";
import { RequestStatus } from "../../helpers/RequestStatus";
import { blueTheme } from "../../helpers/themes";
import {
  App,
  AuthorizationLevel,
  Bank,
  Company,
  Department,
  ExtHrEmployee,
  Role,
  User,
  UserApp,
  UserPostBody,
} from "../../model";
import Select from "react-select";
import {
  AuthserverPph21MarriageCategories,
  AuthserverPph21MarriageCategory,
  AuthserverUser_AuthorizationLevel,
  authserverUser_AuthorizationLevelFromJSON,
  authserverUser_AuthorizationLevelToJSON,
} from "../../masterbigsystem";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { render } from "react-dom";
import { v4 } from "uuid";

const UserDetail = () => {
  const canCreate = usePermission(PERMISSIONS.USERS_CREATE);
  const canUpdate = usePermission(PERMISSIONS.USERS_EDIT);
  const canDelete = usePermission(PERMISSIONS.USERS_DELETE);
  const { id }: { id: string } = useParams();
  const history = useHistory();
  const [user, setUser] = useState<User>({ ...initialUser });
  const [userApps, setUserApps] = useState<UserApp[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [ctx, dispatch] = useAppState() ?? [];
  const [requestStatus, setRequestStatus] = useState<RequestStatus>("NotAsked");
  const [saveStatus, setSaveStatus] = useState<RequestStatus>("NotAsked");
  const [message, setMessage] = useState("");
  const [changePassword, setChangePassword] = useState(false);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [extHrEmployees, setExtHrEmployees] = useState<ExtHrEmployee[]>([]);
  const [extHrEmployeesDetail, setExtHrEmployeesDetail] = useState<
    ExtHrEmployee[]
  >([]);
  const userDepartmentDeleteIds = useRef([] as number[]);
  const terminationTimestamp = useRef(null as string | null);

  // Password for new user
  const [passwordConfirm, setPasswordConfirm] = useState("");

  // New password for update user
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const companies = useRef([] as Company[]);
  const [extTalentaUsers, setExtTalentaUsers] = useState<ExtTalentaUser[]>([]);

  const [, refresh] = useState(false);

  const render = () => {
    refresh((n) => !n);
  };

  const pph21Settings = useRef(
    AuthserverPph21MarriageCategories.fromPartial({ categories: [] }),
  );

  const fetchPphData = async () => {
    const d = await fetchPph21MarriageCategories(ctx?.apiKey ?? "");

    if (d) {
      pph21Settings.current = d;

      render();
    }
  };

  const fetchCompanyData = async () => {
    const d = await fetchCompanies(ctx?.apiKey ?? "");

    if (d) {
      companies.current = d;
      render();
    }
  };

  useEffect(() => {
    const baseUrl = ctx?.baseUrl ?? process.env.REACT_APP_BASE_URL ?? "";
    const apiKey = ctx?.apiKey ?? localStorage.getItem("apiKey") ?? "";
    const numericId = parseInt(id);
    const isExistingUser = !isNaN(numericId);

    setRequestStatus("Loading");

    fetchPphData();
    fetchCompanyData();

    (async () => {
      try {
        const [
          departmentData,
          roleData,
          appData,
          bankData,
          hrEmployees,
          hrEmployeesDetail,
          talentaUsers,
        ] = await Promise.all([
          fetchDepartments(baseUrl, apiKey),
          fetchRoles(baseUrl, apiKey),
          fetchApps(baseUrl, apiKey),
          fetchBanks({ baseUrl, apiKey }),
          fetchHrEmployees(baseUrl, apiKey),
          fetchHrEmployeesDetail(baseUrl, apiKey),
          fetchExtTalentaUsers(baseUrl, apiKey),
        ]);

        setDepartments(departmentData);
        setRoles(roleData);
        setApps(appData);
        setBanks(bankData);
        setExtHrEmployees(hrEmployees);
        setExtHrEmployeesDetail(hrEmployeesDetail);
        setExtTalentaUsers(talentaUsers);

        if (isExistingUser) {
          const [userData, userAppData] = await Promise.all([
            fetchUser(numericId, baseUrl, apiKey),
            fetchUserApps(numericId, baseUrl, apiKey),
          ]);
          if (userData) setUser(userData);
          setUserApps(userAppData);
        }

        setRequestStatus("Success");
      } catch (error) {
        console.error(error);
        setMessage("Failed to load user form data.");
        setRequestStatus("Error");
      }
    })();
  }, []);

  const handleSave = async () => {
    const baseUrl = ctx?.baseUrl ?? process.env.REACT_APP_BASE_URL ?? "";
    const apiKey = ctx?.apiKey ?? localStorage.getItem("apiKey") ?? "";
    setSaveStatus("Loading");
    setMessage("");
    try {
      const response = await fetch(`${baseUrl}/users/save`, {
        method: "post",
        headers: {
          authorization: apiKey,
          "content-type": "application/json",
        },
        body: JSON.stringify({
          user: {
            ...user,
            department:
              user.departmentId !== 0
                ? { ...initialDepartment, id: user.departmentId }
                : null,
          },
          changePassword: changePassword,
          newPassword: newPassword,
          userEnteredPassword: user.password,
        } as UserPostBody),
      });

      if (response.status !== 201) throw new Error(await response.text());

      const userResp = (await response.json()) as User;

      // Save user apps
      await Promise.all(
        userApps.map(async (userApp) => {
          try {
            const resp = await fetch(`${baseUrl}/userapps`, {
              method: "post",
              headers: {
                authorization: apiKey,
                "content-type": "application/json",
              },
              body: JSON.stringify({
                ...userApp,
                user: userResp,
              }),
            });
            if (!resp.ok) throw new Error(await resp.text());
          } catch (e) {
            console.error(e);
            throw e;
          }
        }),
      );

      // user departmnet delete ids
      await Promise.all(
        userDepartmentDeleteIds.current.map(async (id) => {
          try {
            const resp = await fetch(`${baseUrl}/userdepartments/${id}`, {
              method: "delete",
              headers: {
                authorization: apiKey,
                "content-type": "application/json",
              },
            });
            if (!resp.ok) throw new Error(await resp.text());
          } catch (e) {
            console.error(e);
            throw e;
          }
        }),
      );

      setSaveStatus("Success");
      setMessage("User saved successfully.");
      history.push("/");
    } catch (e) {
      console.error(e);
      setSaveStatus("Error");
      setMessage(e instanceof Error ? e.message : "Failed to save user.");
    }
  };

  return (
    <>
      <ThemeProvider theme={blueTheme}>
        <Box m={3}>
          <Box display="flex" flexWrap={1}>
            <Typography variant="h5">User Detail {id}</Typography>
            <Box mx={1}>
              <Button
                disabled={
                  saveStatus === "Loading" ||
                  (user.id === 0 ? !canCreate : !canUpdate) ||
                  (user && user.id !== 0
                    ? changePassword &&
                      (newPassword === "" || newPassword !== newPasswordConfirm) // If user exists, check password change & empty
                    : user.username === "" ||
                      user.password === "" ||
                      user.password !== passwordConfirm) // If user does not exist, change empty pasword & username
                }
                onClick={handleSave}
                variant="contained"
                color="primary"
              >
                {saveStatus === "Loading" ? "Saving..." : "Save"}
              </Button>
            </Box>
            {requestStatus === "Loading" ? (
              <CircularProgress disableShrink size={25} />
            ) : (
              <></>
            )}
          </Box>

          <Box display="flex" my={1} flexWrap="wrap">
            <Box flexGrow={1} mx={1}>
              <TextField
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                value={user.name}
                label="Name"
                fullWidth
              />
            </Box>
            <Box flexGrow={1} mx={1}>
              <TextField
                onChange={(e) => setUser({ ...user, username: e.target.value })}
                value={user.username}
                label="Username"
                fullWidth
              />
            </Box>
            <Box flexGrow={1} mx={1}>
              <TextField
                onChange={(e) => setUser({ ...user, email: e.target.value })}
                value={user.email}
                label="Email"
                fullWidth
              />
            </Box>
            <Box flexGrow={1} mx={1}>
              <Box display="flex" alignItems="end">
                <Box>+</Box>
                <Box mx={1}>
                  <TextField
                    onChange={(e) =>
                      setUser({ ...user, countryCode: e.target.value })
                    }
                    value={user.countryCode}
                    label="Code"
                    style={{ width: 50 }}
                  />
                </Box>

                {/* <TextField
                  onChange={(e) => setUser({ ...user, phone: e.target.value })}
                  value={user.phone}
                  label="Phone"
                  fullWidth
                /> */}
              </Box>
            </Box>
          </Box>

          <Box display="flex" my={2} flexWrap="wrap">
            {/* <Box flexGrow={1}>
              <TextField
                size="small"
                variant="outlined"
                onChange={(e) =>
                  setUser({ ...user, addressLong: e.target.value })
                }
                value={user.addressLong}
                label="Address"
                fullWidth
                multiline
              />
            </Box> */}
            <div className="d-flex">
              {companies.current.map((c) => {
                return (
                  <>
                    <div>
                      <button
                        className={`btn btn-sm ${
                          user.company?.id === c.id
                            ? `btn-primary`
                            : `btn-outline-primary`
                        }`}
                        onClick={() => {
                          setUser({ ...user, company: c });
                        }}
                      >
                        {c.name}
                      </button>
                    </div>
                  </>
                );
              })}
            </div>

            <Box flexGrow={1}>
              <TextField
                size="small"
                variant="outlined"
                onChange={(e) => {
                  setUser({ ...user, allocation: e.target.value });
                }}
                value={user.allocation}
                label="Allocation..."
                fullWidth
                multiline
              />
            </Box>

            <Box flexGrow={1}>
              <TextField
                size="small"
                variant="outlined"
                onChange={(e) => {
                  setUser({ ...user, employeeSerialNumber: e.target.value });
                }}
                value={user.employeeSerialNumber}
                label="Employee ID Number..."
                fullWidth
                multiline
              />
            </Box>

            <Box flexGrow={1}>
              <TextField
                size="small"
                variant="outlined"
                onChange={(e) => {
                  setUser({ ...user, bankAccountNumber: e.target.value });
                }}
                value={user.bankAccountNumber}
                label="Bank account number"
                fullWidth
                multiline
              />
            </Box>

            <Box flexGrow={1}>
              <Autocomplete
                options={banks.sort(
                  (a, b) => a?.code?.localeCompare(b?.code ?? "") ?? 0,
                )}
                onChange={(_, bank) => {
                  setUser({ ...user, bank: bank as Bank });
                }}
                value={user.bank}
                getOptionLabel={(bank) => `${bank?.name} (${bank.code})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    label="Bank"
                    fullWidth
                    multiline
                  />
                )}
              />
            </Box>

            <Box></Box>
          </Box>

          <Box display="flex" my={2} flexWrap="wrap">
            {/* {user.departmentId} */}
            {/* {JSON.stringify(departments.find((department) => department.id === user.departmentId))} */}
            <Autocomplete
              options={departments}
              getOptionLabel={(dept) => dept.name}
              value={
                departments.find(
                  (department) => department.id === user.departmentId,
                ) ?? null
              }
              onChange={(e, dept) => {
                console.log("Dept:", dept);
                setUser({
                  ...user,
                  departmentId: (dept as Department)?.id ?? 0,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  variant="outlined"
                  size="small"
                  style={{ width: 300 }}
                />
              )}
            />

            <Box display="flex" alignItems="center" ml={2}>
              <Typography>Is Head?</Typography>

              <Checkbox
                checked={user.isHead ?? false}
                onClick={() => setUser({ ...user, isHead: !user.isHead })}
              />
            </Box>

            <Box display="flex" alignItems="center" ml={2}>
              <Typography>Job Entry Allowed?</Typography>

              <Checkbox
                checked={user.jobEntryAllowed ?? false}
                onClick={() =>
                  setUser({ ...user, jobEntryAllowed: !user.jobEntryAllowed })
                }
              />
            </Box>

            <Box display="flex" alignItems="center" ml={2}>
              <Typography>Subcon?</Typography>

              <Checkbox
                checked={user.isSubcon ?? false}
                onClick={() => setUser({ ...user, isSubcon: !user.isSubcon })}
              />
            </Box>

            <Box display="flex" alignItems="center" ml={2}>
              <Typography>Exclude Glob. Attendance?</Typography>

              <Checkbox
                checked={user.excludeInGlobalAttendance ?? false}
                onClick={() =>
                  setUser({
                    ...user,
                    excludeInGlobalAttendance: !user.excludeInGlobalAttendance,
                  })
                }
              />
            </Box>

            <Box display="flex" alignItems="center" ml={2}>
              <Typography>is HR?</Typography>

              <Checkbox
                checked={user.isHr ?? false}
                onClick={() =>
                  setUser({
                    ...user,
                    isHr: !user.isHr,
                  })
                }
              />
            </Box>

            <Box display="flex" alignItems="center" ml={2}>
              <Typography>Edit attendance note?</Typography>

              <Checkbox
                checked={user.editAttendanceNoteAllowed ?? false}
                onClick={() =>
                  setUser({
                    ...user,
                    editAttendanceNoteAllowed: !user.editAttendanceNoteAllowed,
                  })
                }
              />
            </Box>
          </Box>

          <div>
            <strong>Multi Department</strong>
          </div>
          <div className="d-flex" style={{ width: 300 }}>
            <Select
              placeholder="Select Dept.."
              options={departments.map((d) => ({
                label: `${d.name}`,
                value: d,
              }))}
              onChange={(v) => {
                const val = v as { value: Department };

                const foundDept = user.userDepartments.find(
                  (dx) => `${dx.departmentId}` === `${val.value.id}`,
                );

                if (!foundDept) {
                  user.userDepartments = [
                    ...user.userDepartments,
                    {
                      ...initialUserDepartment,
                      uuid: v4(),
                      departmentId: val.value.id,
                    },
                  ];
                }

                render();
              }}
            />

            <div className="mx-2 d-flex">
              {user.userDepartments.map((d) => {
                const foundDept = departments.find(
                  (dx) => `${dx.id}` === `${d.departmentId}`,
                );
                return (
                  <>
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        user.userDepartments = user.userDepartments.filter(
                          (dx) => dx.departmentId !== d.departmentId,
                        );

                        userDepartmentDeleteIds.current = [
                          ...userDepartmentDeleteIds.current,
                          d.id,
                        ];

                        render();
                      }}
                      className="border border-dark shadow shadow-md bg-light rounded rounded-md mx-1"
                    >
                      {foundDept?.name}
                    </div>
                  </>
                );
              })}
            </div>
          </div>

          <div>
            <small>Authorization Level</small>
          </div>
          <div className="d-flex">
            <div>
              <Select
                placeholder="Auth level"
                options={[
                  AuthserverUser_AuthorizationLevel.DIRECTOR,
                  AuthserverUser_AuthorizationLevel.MANAGER,
                  AuthserverUser_AuthorizationLevel.SUPERVISOR,
                  AuthserverUser_AuthorizationLevel.TEAM_LEAD,
                  AuthserverUser_AuthorizationLevel.OPERATOR,
                  AuthserverUser_AuthorizationLevel.STAFF,
                ].map((s) => ({
                  label: authserverUser_AuthorizationLevelToJSON(s),
                  value: s,
                }))}
                onChange={(v) => {
                  setUser({
                    ...user,
                    authorizationLevel: v?.value
                      ? (authserverUser_AuthorizationLevelToJSON(
                          v.value,
                        ) as AuthorizationLevel)
                      : user.authorizationLevel,
                  });
                }}
              />
            </div>

            <div className="mx-2">{user.authorizationLevel}</div>
          </div>

          <Box display="flex" my={2} flexWrap="wrap" alignItems="center">
            <Autocomplete
              options={roles}
              getOptionLabel={(role) => role.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Roles"
                  variant="outlined"
                  size="small"
                  style={{ width: 300 }}
                />
              )}
              onChange={(e, role) => {
                console.log(role);

                if (role)
                  setUser({
                    ...user,
                    roles: user.roles.find(
                      (rolex) => rolex.id === (role as Role)?.id,
                    )
                      ? user.roles
                      : (role as Role)
                        ? [...user.roles, role as Role]
                        : user.roles,
                  });
              }}
            />
            <Box display="flex" flexWrap="wrap " mx={2}>
              <Typography>Selected:</Typography>
              {user.roles.map((role) => (
                <Box
                  mx={1}
                  px={1}
                  style={{
                    cursor: "pointer",
                    color: "white",
                    backgroundColor: blueGrey[500],
                  }}
                  borderRadius={5}
                  onClick={() => {
                    setUser({
                      ...user,
                      roles: user.roles.filter((rolex) => rolex.id !== role.id),
                    });
                  }}
                >
                  <Typography style={{ fontWeight: "bold" }}>
                    {role.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Autocomplete
              options={apps}
              getOptionLabel={(app) => app.name}
              onChange={(e, app) => {
                if (
                  filterNotHiddenAndSort(userApps).find(
                    (userApp) => userApp.app?.id === (app as App)?.id,
                  )
                ) {
                  // If app found, skip
                } else {
                  if (app)
                    setUserApps([
                      ...userApps,
                      { ...initialUserApp, user: user, app: app as App },
                    ]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Apps Whitelist"
                  variant="outlined"
                  size="small"
                  style={{ width: 300 }}
                />
              )}
            />

            <Box mx={2}>
              <Typography>Selected:</Typography>
            </Box>

            {filterNotHiddenAndSort(userApps).map((userApp, i) => (
              <>
                <Box
                  mx={1}
                  px={1}
                  style={{
                    cursor: "pointer",
                    color: "white",
                    backgroundColor: blueGrey[500],
                  }}
                  borderRadius={5}
                  onClick={() => {
                    const newUserApps = [...userApps];

                    let foundUserApp = filterNotHiddenAndSort(newUserApps).find(
                      (_, ix) => i === ix,
                    );

                    console.log("new user apps:", newUserApps, foundUserApp);

                    if (foundUserApp) {
                      foundUserApp.hidden = true;
                      setUserApps(newUserApps);
                    }
                  }}
                >
                  <Typography style={{ fontWeight: "bold" }}>
                    {userApp.app?.name}
                  </Typography>
                </Box>
              </>
            ))}
          </Box>

          <Box my={2} display="flex" flexWrap={1}>
            <TextField
              label="Device Unique ID"
              variant="outlined"
              size="small"
              value={user.deviceUniqueId ?? ""}
              onChange={(e) => {
                setUser({ ...user, deviceUniqueId: e.target.value ?? "" });
              }}
            />

            {/* HR Employee */}
            <Box mx={1} flexGrow={1}>
              <Autocomplete
                options={extHrEmployees ?? []}
                getOptionLabel={(e) =>
                  `${e.firstName ?? ""}${
                    e.middleName ? ` ${e.middleName}` : ""
                  }${e.lastName ? ` ${e.lastName}` : ""}`
                }
                onChange={(_, employee) => {
                  if (employee) {
                    setUser({
                      ...user,
                      hrId: (employee as ExtHrEmployee).id,
                    });
                  } else {
                    setUser({
                      ...user,
                      hrId: null,
                    });
                  }
                }}
                value={
                  extHrEmployees.find(
                    (extHrEmployee) => extHrEmployee.id === user.hrId,
                  ) ?? null
                }
                size="small"
                renderInput={(params) => (
                  <TextField
                    label={`HR Employee`}
                    variant="outlined"
                    {...params}
                  />
                )}
              />
            </Box>

            {/* Talenta Employee */}
            <Box mx={1} flexGrow={1}>
              <Autocomplete
                options={extTalentaUsers ?? []}
                getOptionLabel={(u) => {
                  const fn = u?.personal?.first_name ?? "";
                  const ln = u?.personal?.last_name ?? "";
                  const org = u?.employment?.organization_name ?? "";
                  const job = u?.employment?.job_position ?? "";
                  const name = `${fn}${fn && ln ? " " : ""}${ln}`.trim();
                  const tail = `${org}${org && job ? " / " : ""}${job}`.trim();
                  return `${name}${tail ? ` — ${tail}` : ""}`;
                }}
                onChange={(_, talentaUser) => {
                  if (talentaUser) {
                    setUser({
                      ...user,
                      talentaId: (talentaUser as ExtTalentaUser).user_id,
                    });
                  } else {
                    setUser({
                      ...user,
                      talentaId: null,
                    });
                  }
                }}
                value={
                  extTalentaUsers.find((u) => u.user_id === user.talentaId) ??
                  null
                }
                size="small"
                renderInput={(params) => (
                  <TextField
                    label={`Talenta Employee`}
                    variant="outlined"
                    {...params}
                  />
                )}
              />
            </Box>
          </Box>

          {user.hrId ? (
            <Box>
              {(() => {
                const foundUser = extHrEmployees.find(
                  (extUserEmployee) => extUserEmployee.id === user.hrId,
                );
                const foundUserDetail = extHrEmployeesDetail.find(
                  (extUserEmployee) => extUserEmployee.id === user.hrId,
                );

                return foundUser ? (
                  <>
                    <Box>
                      <strong>Name: </strong> {foundUser.firstName}
                      {foundUser.middleName ? ` ${foundUser.middleName}` : ""}
                      {foundUser.lastName ? ` ${foundUser.lastName}` : ""}
                    </Box>
                    <Box>
                      <strong>Address: </strong>
                      {foundUser.location?.address}
                    </Box>
                    <Box>
                      <strong>Phone: </strong>
                      {foundUser?.contact?.find((c) => c.hp && c.hp !== "")?.hp}
                    </Box>
                    <Box>
                      <strong>Entry date: </strong>
                      {(() => {
                        try {
                          return Intl.DateTimeFormat("en-US", {
                            dateStyle: "medium",
                          } as any).format(
                            new Date(foundUserDetail?.empJoinDate ?? ""),
                          );
                        } catch (e) {
                          return "";
                        }
                      })()}
                    </Box>
                  </>
                ) : (
                  <></>
                );
              })()}
            </Box>
          ) : (
            <></>
          )}
          {/* {JSON.stringify(
            extHrEmployees.find(
              (extHrEmployee) => extHrEmployee.id === user.hrId
            )
          )} */}

          {/* <Box overflow="auto">
            <Typography variant="caption">
              <pre>{JSON.stringify(userApps, null, 2)}</pre>
            </Typography>
          </Box> */}

          {user.id && user.id !== 0 ? (
            <>
              <Box display="flex" alignItems="center">
                <Box>
                  <Typography>Change password?</Typography>
                </Box>
                <Box>
                  <Checkbox
                    checked={changePassword}
                    onClick={() => setChangePassword(!changePassword)}
                  />
                </Box>
              </Box>
              <Box display="flex" alignItems="end">
                {changePassword ? (
                  <>
                    <Box>
                      <TextField
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        label="New Password"
                        type="password"
                        multiline
                      />
                    </Box>

                    <Box mx={1}>
                      <TextField
                        value={newPasswordConfirm}
                        onChange={(e) => setNewPasswordConfirm(e.target.value)}
                        label="Confirm Password"
                        type="password"
                        multiline
                      />
                    </Box>

                    {newPassword !== newPasswordConfirm ? (
                      <Typography color="error">
                        Passwords do not match!
                      </Typography>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </Box>
            </>
          ) : (
            <>
              <Box display="flex" alignItems="end" flexWrap="wrap">
                <Box>
                  <TextField
                    onChange={(e) =>
                      setUser({ ...user, password: e.target.value })
                    }
                    value={user.password}
                    label="Password"
                    type="password"
                    multiline
                  />
                </Box>

                <Box mx={1}>
                  <TextField
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    value={passwordConfirm}
                    label="Confirm Password"
                    type="password"
                    multiline
                  />
                </Box>

                {user.password !== passwordConfirm ? (
                  <Typography color="error">Passwords do not match!</Typography>
                ) : (
                  <></>
                )}
              </Box>
            </>
          )}

          <Divider />

          <Box my={1} py={1} display="flex">
            <Box>
              <TextField
                type="date"
                variant="outlined"
                size="small"
                label="COVID vaccine 1 date"
                InputLabelProps={{ shrink: true }}
                value={
                  user.vaccine1
                    ? makeDateStringUTC(new Date(user.vaccine1))
                    : null
                }
                onChange={(e) => {
                  if (e.target.value && e.target.value !== "")
                    setUser({
                      ...user,
                      vaccine1: `${e.target.value}T00:00:00Z`,
                    });
                }}
              />
            </Box>
            <Box>
              <Box display="flex" justifyContent="center" mx={1}>
                <Typography>Done?</Typography>{" "}
              </Box>
              <Box display="flex" justifyContent="center">
                <Checkbox
                  checked={user.vaccine1Done ?? false}
                  onClick={(e) => {
                    setUser({
                      ...user,
                      vaccine1Done: !(user.vaccine1Done ?? false),
                    });
                  }}
                />
              </Box>
            </Box>
            <Box mx={2}>
              <TextField
                type="date"
                variant="outlined"
                size="small"
                label="COVID vaccine 2 date"
                InputLabelProps={{ shrink: true }}
                value={
                  user.vaccine2
                    ? makeDateStringUTC(new Date(user.vaccine2))
                    : null
                }
                onChange={(e) => {
                  if (e.target.value && e.target.value !== "")
                    setUser({
                      ...user,
                      vaccine2: `${e.target.value}T00:00:00Z`,
                    });
                }}
              />
            </Box>
            <Box>
              <Box display="flex" justifyContent="center" mx={1}>
                <Typography>Done?</Typography>{" "}
              </Box>
              <Box display="flex" justifyContent="center">
                <Checkbox
                  checked={user.vaccine2Done ?? false}
                  onClick={(e) => {
                    setUser({
                      ...user,
                      vaccine2Done: !(user.vaccine2Done ?? false),
                    });
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* <div>
            <strong>Entry Date</strong>
          </div>

          <Box className="my-2">
            <TextField
              type="date"
              variant="outlined"
              size="small"
              label="Entry date"
              InputLabelProps={{ shrink: true }}
              value={
                user.entryDate
                  ? makeDateStringUTC(new Date(user.entryDate))
                  : null
              }
              onChange={(e) => {
                if (e.target.value && e.target.value !== "")
                  setUser({
                    ...user,
                    entryDate: `${e.target.value}T00:00:00Z`,
                  });
              }}
            />
          </Box> */}

          <div>
            <strong>PPH Status</strong>
          </div>
          <div>
            <div>
              <Select
                placeholder="PPH 21 setting..."
                options={pph21Settings.current.categories.map((c) => ({
                  label: c.name,
                  value: c,
                }))}
                value={pph21Settings.current.categories
                  .map((c) => ({
                    label: c.name,
                    value: c,
                  }))
                  .find(
                    (c) =>
                      `${c.value.masterJavaBaseModel?.id}` ===
                      `${user.pph21MarriageCategory?.id}`,
                  )}
                onChange={(v) => {
                  const val = v as { value: AuthserverPph21MarriageCategory };
                  setUser({
                    ...user,
                    pph21MarriageCategory: {
                      ...initialPph21,
                      id: val.value.masterJavaBaseModel?.id
                        ? isNaN(parseInt(val.value.masterJavaBaseModel?.id))
                          ? 0
                          : parseInt(val.value.masterJavaBaseModel?.id)
                        : 0,
                    },
                  });
                }}
              />
            </div>
          </div>

          <Box my={2}>
            <Divider />
          </Box>

          <hr className="border border-dark" />

          <div>
            <h4>Employee home</h4>
          </div>

          {/* <div>
            <input
              className="form-control form-control-sm"
              onBlur={() => {}}
              placeholder="Search location name..."
            />
          </div> */}

          <div className="d-flex">
            <div className="flex-grow-1">
              <div>
                <div>Lat</div>
                <div>
                  <input
                    className="form-control form-control-sm"
                    onBlur={(e) => {
                      setUser({
                        ...user,
                        lat: isNaN(parseFloat(e.target.value))
                          ? user.lat
                          : parseFloat(e.target.value),
                      });
                    }}
                    placeholder="Lat..."
                  />
                </div>
                <div>{user.lat}</div>
              </div>
            </div>
            <div className="flex-grow-1">
              <div>
                <div>Lon</div>
                <div>
                  {" "}
                  <input
                    className="form-control form-control-sm"
                    onBlur={(e) => {
                      setUser({
                        ...user,
                        lon: isNaN(parseFloat(e.target.value))
                          ? user.lon
                          : parseFloat(e.target.value),
                      });
                    }}
                    placeholder="Lon..."
                  />
                  {user.lon}
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: "35vh", width: "100%" }}>
            <MapContainer
              center={[user.lat ?? -6.227026, user.lon ?? 106.816581]}
              zoom={11}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker
                position={[user.lat ?? -6.227026, user.lon ?? 106.816581]}
              >
                <Popup>Location of {user.username}.</Popup>
              </Marker>
            </MapContainer>
          </div>

          <Box>
            <Typography variant="h5">Termination</Typography>
          </Box>

          <Box>
            {user?.hidden ? (
              <>
                <div className="d-flex">
                  <Button
                    disabled={!canDelete}
                    onClick={async () => {
                      const confirmation = window.confirm(`Undo termination?`);

                      if (confirmation) {
                        const resp = await fetch(
                          `${ctx?.baseUrl}/users/${user.id}/show`,
                          {
                            method: "post",
                            headers: { authorization: ctx?.apiKey ?? "" },
                          },
                        );

                        alert("Employee redone successfully.");
                        history.push("/");
                      } else {
                        alert("Employee not re-opened.");
                      }
                    }}
                    color="primary"
                    variant="contained"
                  >
                    Undo
                  </Button>
                  {user.terminationTimestamp ? (
                    <>
                      {(() => {
                        try {
                          return intlFormat({
                            date: user.terminationTimestamp,
                            dateStyle: "long",
                            timeStyle: "medium",
                          });
                        } catch (e) {
                          return "";
                        }
                      })()}
                    </>
                  ) : (
                    <> </>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="d-flex">
                  <Button
                    disabled={!canDelete}
                    onClick={async () => {
                      const prompt = window.prompt(
                        `Really terminate employee ${user.name}? Please write down the employee's full name or username (case sensitive).`,
                      );

                      if (
                        prompt &&
                        prompt !== "" &&
                        (prompt === user.name || prompt === user.username)
                      ) {
                        console.log(`${terminationTimestamp.current}T00:00:00`);
                        const resp = await fetch(
                          `${ctx?.baseUrl}/users/${
                            user.id
                          }/hide?terminationTimestamp=${
                            terminationTimestamp.current ?? ""
                          }`,
                          {
                            method: "delete",
                            headers: { authorization: ctx?.apiKey ?? "" },
                          },
                        );

                        alert("Employee terminated successfully.");
                        history.push("/");
                      } else {
                        alert("Employee not terminated.");
                      }
                    }}
                    color="secondary"
                    variant="contained"
                  >
                    Terminate
                  </Button>
                  <input
                    style={{ width: 150 }}
                    type="date"
                    className="form-control form-control-sm"
                    defaultValue={terminationTimestamp.current ?? undefined}
                    onBlur={(e) => {
                      if (e.target.value === "") {
                        return;
                      }

                      terminationTimestamp.current = new Date(
                        e.target.value,
                      ).toISOString();
                      render();
                    }}
                  />
                </div>
              </>
            )}
          </Box>
        </Box>
        <Snackbar
          open={Boolean(message)}
          autoHideDuration={3000}
          onClose={() => setMessage("")}
          message={message}
        />
      </ThemeProvider>
    </>
  );
};

export default UserDetail;
